/**
 * Admin resume / open-step routes for Local Bridge sessions.
 * Status → Continue destination; Open step menu for facilitator jumps.
 */

export type BridgeSessionListLike = {
  id: string;
  status: string;
  domain?: string | null;
};

export type BridgeSessionOpenStep = {
  key: string;
  label: string;
  pathFor: (sessionId: string) => string;
};

/** Ordered bridge-session UI steps (sidebar / Open step menu). */
export const BRIDGE_SESSION_STEP_ORDER = [
  'questions',
  'domain_builder',
  'entropy_heat_map',
  'friction_priorities',
  'define_conditions',
  'bridge_card',
  'control_bridge',
] as const;

export type BridgeSessionUiStep = (typeof BRIDGE_SESSION_STEP_ORDER)[number];

export function isBridgeSessionUiStep(value: string): value is BridgeSessionUiStep {
  return (BRIDGE_SESSION_STEP_ORDER as readonly string[]).includes(value);
}

/** True when `candidate` is at or before `current` in the guided flow. */
export function isBridgeSessionStepAtOrBefore(
  candidate: string,
  current: string
): boolean {
  const cIdx = BRIDGE_SESSION_STEP_ORDER.indexOf(candidate as BridgeSessionUiStep);
  const curIdx = BRIDGE_SESSION_STEP_ORDER.indexOf(current as BridgeSessionUiStep);
  if (cIdx < 0 || curIdx < 0) return candidate === current;
  return cIdx <= curIdx;
}

export function resolveBridgeSessionContinuePath(session: BridgeSessionListLike): string {
  const id = session.id;
  const status = (session.status || '').toLowerCase();
  const domain = (session.domain || '').toLowerCase();

  if (status === 'draft') {
    return `/local-bridge/business-objective-workshop?sessionId=${encodeURIComponent(id)}`;
  }

  if (status === 'collecting_current' || status === 'collecting_future') {
    if (domain === 'customer') {
      return `/local-bridge/marketfit/guided?sessionId=${encodeURIComponent(id)}`;
    }
    if (domain === 'product' || domain === 'stakeholder') {
      // Product needs a product-domain session id; stakeholder flow is incomplete —
      // land on domain selector / toll surface.
      return `/local-bridge/control-bridge?sessionId=${encodeURIComponent(id)}`;
    }
    return `/bridge-session/${encodeURIComponent(id)}?step=questions`;
  }

  if (status === 'bridge_built' || status === 'calculated') {
    return `/bridge-session/${encodeURIComponent(id)}?step=bridge_card`;
  }

  return `/local-bridge/business-objective-workshop?sessionId=${encodeURIComponent(id)}`;
}

export function continueActionLabel(session: BridgeSessionListLike): string {
  const status = (session.status || '').toLowerCase();
  if (status === 'draft') return 'Continue workshop';
  if (status === 'bridge_built' || status === 'calculated') return 'Open Bridge Card';
  if (status === 'collecting_current' || status === 'collecting_future') {
    return 'Continue session';
  }
  return 'Continue';
}

/** Facilitator jump targets (Open step menu). */
export const BRIDGE_SESSION_OPEN_STEPS: BridgeSessionOpenStep[] = [
  {
    key: 'workshop',
    label: 'Business Objective Workshop',
    pathFor: (id) => `/local-bridge/business-objective-workshop?sessionId=${encodeURIComponent(id)}`,
  },
  {
    key: 'domain_selector',
    label: 'Domain selector / toll',
    pathFor: (id) => `/local-bridge/control-bridge?sessionId=${encodeURIComponent(id)}`,
  },
  {
    key: 'questions',
    label: 'Questions',
    pathFor: (id) => `/bridge-session/${encodeURIComponent(id)}?step=questions`,
  },
  {
    key: 'domain_builder',
    label: 'Domain builder',
    pathFor: (id) => `/bridge-session/${encodeURIComponent(id)}?step=domain_builder`,
  },
  {
    key: 'entropy_heat_map',
    label: 'Entropy heat map',
    pathFor: (id) => `/bridge-session/${encodeURIComponent(id)}?step=entropy_heat_map`,
  },
  {
    key: 'friction_priorities',
    label: 'Friction priorities',
    pathFor: (id) => `/bridge-session/${encodeURIComponent(id)}?step=friction_priorities`,
  },
  {
    key: 'define_conditions',
    label: 'Define conditions',
    pathFor: (id) => `/bridge-session/${encodeURIComponent(id)}?step=define_conditions`,
  },
  {
    key: 'bridge_card',
    label: 'Bridge Card',
    pathFor: (id) => `/bridge-session/${encodeURIComponent(id)}?step=bridge_card`,
  },
  {
    key: 'control_bridge_dashboard',
    label: 'Control Bridge dashboard',
    pathFor: (id) => `/dashboard?sessionId=${encodeURIComponent(id)}`,
  },
  {
    key: 'marketfit',
    label: 'MarketFit guided',
    pathFor: (id) => `/local-bridge/marketfit/guided?sessionId=${encodeURIComponent(id)}`,
  },
];
